import { getServerBlockType } from "@pathwright/blocks-core"
import {
  EDITOR_LAYERING,
  EDITOR_MODE,
  useEditorBlock,
  useEditorBlocks,
  useEditorBlocksContext,
  useEditorDispatch,
  useEditorMode,
  useEditorUI
} from "@pathwright/blocks-engine"
import ErrorBoundary from "@pathwright/ui/src/components/error/ErrorBoundary"
import React from "react"
import isEqual from "react-fast-compare"
import styled, { keyframes } from "styled-components"
import { defaultBlockLayoutStyles } from "../editor/BlockStyles/base-style-defaults"
import AddBlocks from "./AddBlocks"
import BlockControls from "./BlockControls"
import BlockEditor from "./BlockEditor"
import BlockErrorHandler from "./BlockErrorHandler"
import DeleteBlockPrompt from "./DeleteBlockPrompt"

const blockIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px) scale(.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`

const StyledBlockEditorContainerWrap = styled.div`
  position: relative;
  z-index: ${(p) => (p.isFocused ? EDITOR_LAYERING.FOCUSED_BLOCK : "")};

  .ContentBlock {
    animation: ${blockIn} 0.33s;
  }

  /* Prevent menus (ex: Styles) from remaining open over a subsequent block */
  &:not(:hover) .BlockControls {
    display: none;
  }

  .BlockControls {
    opacity: 0;
    pointer-events: none;
  }

  &:hover {
    .AddBlocks {
      opacity: 1;
      &:not(.AddBlocks--open) .AddBlocks__Icon {
        z-index: ${EDITOR_LAYERING.HOVERED_ADD_BLOCK};
      }
    }

    .BlockControls {
      opacity: 1;
      z-index: ${EDITOR_LAYERING.HOVERED_BLOCK_CONTROLS};
    }
  }
`

const BlockEditorContainerWrap = ({ children, ...props }) => {
  const { focusedBlockIndex } = useEditorUI()

  const { isURLFocused } = props

  const isFocused = focusedBlockIndex === props.blockIndex

  return (
    <StyledBlockEditorContainerWrap
      {...props}
      isFocused={isFocused}
      isHighlighted={isURLFocused}
    >
      {children}
    </StyledBlockEditorContainerWrap>
  )
}

const propsAreEqual = (prevProps, nextProps) => {
  if (nextProps.layout !== prevProps.layout) return false
  if (nextProps.order !== prevProps.order) return false
  if (nextProps.block.order !== prevProps.block.order) return false
  if (nextProps.deleting !== prevProps.deleting) return false
  if (!isEqual(nextProps.blockType, prevProps.blockType)) return false
  if (!isEqual(nextProps.block, prevProps.block)) return false
  if (nextProps.blockIndex !== prevProps.blockIndex) return false
  if (!isEqual(nextProps.blocks, prevProps.blocks)) return false

  return true
}

const BlockEditorContainer = React.memo((props) => {
  const {
    block,
    deleting,
    blockType,
    blockIndex,
    onCopyBlock,
    renderTopAddBlocks,
    renderBottomAddBlocks
  } = props

  const hash = window.location.hash.replace("/", "").replace("#", "")

  const { mode } = useEditorMode()
  const {
    handleMoveUp: onMoveUp,
    handleMoveDown: onMoveDown,
    handleLayoutChange: onChangeLayout,
    handleBlockDelete: onDelete,
    handleCancelBlockDelete: onCancelDelete
  } = useEditorDispatch()

  const handleMoveUp = React.useCallback(() => {
    onMoveUp(blockIndex, block.id)
  }, [blockIndex, block])

  const handleMoveDown = React.useCallback(() => {
    onMoveDown(blockIndex, block.id)
  }, [blockIndex, block])

  const handleDelete = React.useCallback(() => {
    onDelete(block.id)
  }, [block])

  const handleCopy = React.useCallback(() => {
    onCopyBlock(block)
  }, [block])

  const handleSelectLayout = React.useCallback(
    (layout) => {
      onChangeLayout(block.id, layout)
    },
    [block]
  )

  const handleCancel = React.useCallback(() => {
    onCancelDelete(blockIndex)
  }, [blockIndex])

  return (
    <BlockEditorContainerWrap
      key={`${block.contentID}:${block.id}`}
      className={`BlockEditorContainer block-mode-${mode} Block-${blockIndex} block-type-${block.type}-${block.layout}`}
      blockIndex={blockIndex}
      id={`block-${block.id}`}
      isURLFocused={hash === block.id}
    >
      {deleting ? (
        <DeleteBlockPrompt onCancel={handleCancel} onDelete={handleDelete} />
      ) : null}
      {renderTopAddBlocks()}
      {mode === EDITOR_MODE.EDIT ? (
        <BlockControls
          blockIndex={blockIndex}
          autoFocus={!block.createdDateTime}
          onCopy={handleCopy}
          onDelete={handleDelete}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
          onSelectLayout={handleSelectLayout}
          block={block}
          blockType={blockType}
        />
      ) : null}
      <BlockEditor
        blockIndex={blockIndex}
        blockType={blockType}
        block={block}
      />
      {renderBottomAddBlocks()}
    </BlockEditorContainerWrap>
  )
}, propsAreEqual)

const ContentEditorBlocks = () => {
  const { mode } = useEditorMode()
  const { deleteBlockID } = useEditorBlock()
  const { blocks } = useEditorBlocks()
  const { blockTypes } = useEditorBlocksContext()
  const { handleAddBlock: onAddBlock, handleCopyBlock: onCopyBlock } =
    useEditorDispatch()

  const handleAddBlock = (blockIndex) => (type, data, layout) => {
    const defaultStyles = defaultBlockLayoutStyles[layout] || null

    onAddBlock(type, blockIndex + 1, data, layout, defaultStyles)
  }

  const renderAddBlocks = (
    blockIndex,
    placement,
    show,
    { forceOpen = false, isLastBlock = false }
  ) => {
    if (!show) return null

    return (
      <AddBlocks
        key={`add-blocks-${placement}-${blockIndex}`}
        onAddBlock={handleAddBlock(blockIndex)}
        blockIndex={blockIndex}
        top={placement === "top"}
        forceOpen={forceOpen}
        onCancelPaste={() => onCopyBlock(null)}
        isLastBlock={isLastBlock}
      />
    )
  }

  return (
    <div style={{ position: "relative", zIndex: "1" }} className="ContentEditorBlocks">
      {blocks && blocks.length
        ? blocks.map((block, i) => {
            const isLastBlock = i === blocks.length - 1

            const deleting = deleteBlockID === block.id
            const showAddBlocks = mode === EDITOR_MODE.EDIT && !deleting

            const blockType = block.type
              ? getServerBlockType(block.type, blockTypes)
              : null
            if (!blockType) {
              console.error(`Unknown block type found: ${block.type}`)
              return null
            }
            return (
              <ErrorBoundary
                key={block.id}
                // logToSentry={false}
                catchLoadingChunkFailures={false}
                renderError={(error, resetError) => (
                  <BlockErrorHandler
                    error={error}
                    block={block}
                    allowEdit={true}
                    blockType={blockType}
                    onFixedBlock={resetError}
                  />
                )}
              >
                <BlockEditorContainer
                  blockType={blockType}
                  block={block}
                  blockIndex={i}
                  deleting={deleting}
                  showAddBlocks={showAddBlocks}
                  renderTopAddBlocks={() =>
                    renderAddBlocks(i, "top", showAddBlocks, { isLastBlock })
                  }
                  renderBottomAddBlocks={() =>
                    renderAddBlocks(i + 1, "bottom", showAddBlocks, {
                      isLastBlock
                    })
                  }
                  onCopyBlock={onCopyBlock}
                />
              </ErrorBoundary>
            )
          })
        : renderAddBlocks(0, "top", true, {
            forceOpen: true
          })}
    </div>
  )
}

export default ContentEditorBlocks
